import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { Lock, Unlock, X, DollarSign, AlertTriangle } from 'lucide-react';

// ⭐ Cierre de caja: sesión formal de turno. Se abre con un monto inicial en
// efectivo, y al cerrar se cuenta el efectivo físico y el sistema calcula
// cuánto DEBERÍA haber (monto inicial + ventas en efectivo) para mostrar la
// diferencia (sobrante/faltante) — esto es lo que de verdad detecta errores
// o faltantes de caja, a diferencia de un simple reporte de ventas.
//
// Autocontenido: expone un botón de estado (🔓/🔒) que al hacer clic abre
// el modal con todo el flujo. Se puede montar en cualquier pantalla donde
// se cobra (hoy: CajaVentas.js).
function formatDateTime(value) {
  if (!value) return '';
  // Los timestamps vienen en UTC naive desde SQLite ('YYYY-MM-DD HH:MM:SS').
  // Igual que el resto del POS, se interpretan como si ya estuvieran en
  // hora de Ecuador para evitar el doble-desfase (ver toEcuadorTime en
  // otros componentes) — aquí basta con mostrarlos tal cual los devuelve
  // el backend, sin aritmética de timezone, ya que solo es informativo.
  const d = new Date(value.replace(' ', 'T'));
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('es-EC', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function getStoredClosingWidth() {
  const v = localStorage.getItem('pos_closing_report_width');
  return ['58', '80', 'a4'].includes(v) ? v : '80';
}

// ⭐ Reporte imprimible del cierre de caja. Mismo mecanismo que
// ReciboManager.js (área oculta + CSS @media print + window.print), pero
// con un tercer formato posible (A4) para negocios que prefieran archivar
// el cierre en una impresora de oficina normal en vez de la térmica.
function CierreCajaTicket({ data, businessName, widthKey }) {
  const isA4 = widthKey === 'a4';
  const paperWidth = widthKey === '58' ? '58mm' : widthKey === '80' ? '80mm' : '210mm';
  return (
    <div id="cierre-print-area">
      <style>{`
        #cierre-print-area { display: none; }
        @media print {
          ${isA4 ? '@page { size: A4; margin: 20mm; }' : '@page { margin: 0; }'}
          body * { visibility: hidden !important; }
          #cierre-print-area, #cierre-print-area * { visibility: visible !important; }
          #cierre-print-area {
            display: block !important;
            position: absolute; top: 0; left: 0;
            width: ${paperWidth};
            font-family: ${isA4 ? "Arial, Helvetica, sans-serif" : "'Courier New', Courier, monospace"};
            font-size: ${isA4 ? '13px' : widthKey === '58' ? '10px' : '12px'};
            color: #000;
            padding: ${isA4 ? '0' : '4mm 3mm'};
          }
          .cierre-line { border-top: 1px dashed #000; margin: ${isA4 ? '10px' : '4px'} 0; }
          .cierre-row { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
          .cierre-center { text-align: center; }
          .cierre-bold { font-weight: bold; }
          .cierre-title { font-size: ${isA4 ? '20px' : '13px'}; font-weight: bold; text-align: center; margin-bottom: ${isA4 ? '16px' : '4px'}; }
        }
      `}</style>
      <div className="cierre-center cierre-bold">{businessName || 'Negocio'}</div>
      <div className="cierre-title">CIERRE DE CAJA</div>
      <div className="cierre-line" />
      <div>Abierta: {formatDateTime(data.opened_at)}{data.opened_by_name ? ` — ${data.opened_by_name}` : ''}</div>
      <div>Cerrada: {formatDateTime(data.closed_at)}{data.closed_by_name ? ` — ${data.closed_by_name}` : ''}</div>
      <div className="cierre-line" />
      <div className="cierre-row"><span>Monto inicial</span><span>${Number(data.opening_amount || 0).toFixed(2)}</span></div>
      <div className="cierre-row"><span>Ventas efectivo</span><span>${Number(data.cash_sales_total || 0).toFixed(2)}</span></div>
      <div className="cierre-row"><span>Ventas transferencia</span><span>${Number(data.transfer_sales_total || 0).toFixed(2)}</span></div>
      <div className="cierre-row cierre-bold"><span>Total vendido ({data.total_sales_count || 0} ventas)</span><span>${Number(data.total_sales_amount || 0).toFixed(2)}</span></div>
      <div className="cierre-line" />
      <div className="cierre-row"><span>Efectivo esperado</span><span>${Number(data.expected_cash || 0).toFixed(2)}</span></div>
      <div className="cierre-row"><span>Efectivo contado</span><span>${Number(data.counted_cash || 0).toFixed(2)}</span></div>
      <div className="cierre-row cierre-bold">
        <span>Diferencia</span>
        <span>{data.difference > 0 ? '+' : ''}${Number(data.difference || 0).toFixed(2)}
          {Math.abs(data.difference) < 0.01 ? ' (cuadra)' : data.difference < 0 ? ' (faltante)' : ' (sobrante)'}
        </span>
      </div>
      {data.items_sold?.length > 0 && (
        <>
          <div className="cierre-line" />
          <div className="cierre-bold">Qué se vendió:</div>
          {data.items_sold.map((it, idx) => (
            <div key={idx} className="cierre-row">
              <span>{it.quantity}x {it.name}</span>
              <span>${Number(it.subtotal || 0).toFixed(2)}</span>
            </div>
          ))}
        </>
      )}
      {data.notes && (
        <>
          <div className="cierre-line" />
          <div>Notas: {data.notes}</div>
        </>
      )}
      <div className="cierre-line" />
      <div className="cierre-center">Reporte generado automáticamente al cerrar caja</div>
    </div>
  );
}

export default function CierreCaja({ token, clientData, onSessionChange }) {
  const [session, setSession] = useState(null);
  const [liveSummary, setLiveSummary] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [view, setView] = useState('status'); // 'status' | 'open-form' | 'close-form' | 'close-result'
  const [openingAmount, setOpeningAmount] = useState('');
  const [countedCash, setCountedCash] = useState('');
  const [notes, setNotes] = useState('');
  const [closeResult, setCloseResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [printWidthKey, setPrintWidthKey] = useState('80');

  // ⭐ Impresión OBLIGATORIA del cierre de caja: apenas hay un resultado de
  // cierre exitoso, se dispara window.print() sin pedir confirmación
  // ("si o si" — instrucción explícita del cliente). El ancho se lee fresco
  // de localStorage en este momento (no al montar), para reflejar el
  // último valor guardado en Configuración > Impresión.
  useEffect(() => {
    if (!closeResult) return undefined;
    setPrintWidthKey(getStoredClosingWidth());
    const timer = setTimeout(() => window.print(), 200);
    return () => clearTimeout(timer);
  }, [closeResult]);

  const fetchCurrent = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const res = await axios.get(`${API_URL}/api/pos/cash-sessions/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSession(res.data.session);
      setLiveSummary(res.data.live_summary || null);
    } catch (err) {
      // Si falla (ej. sin permiso), se trata como "estado desconocido" y
      // simplemente no se muestra el botón — no se interrumpe el resto de
      // Caja por esto.
      setSession(null);
      setLiveSummary(null);
    } finally {
      setLoadingStatus(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCurrent();
    const interval = setInterval(fetchCurrent, 60000);
    return () => clearInterval(interval);
  }, [fetchCurrent]);

  // ⭐ Notifica al padre (CajaVentas) si hay o no una caja abierta, sin que
  // tenga que hacer su propio polling redundante al mismo endpoint -- una
  // sola fuente de verdad (este componente) para el estado de la sesión.
  // `loadingStatus` se excluye a propósito: mientras carga, `session` sigue
  // siendo el valor anterior (o null en el primer render), así que igual
  // se notifica algo razonable de inmediato.
  useEffect(() => {
    if (onSessionChange) onSessionChange(session);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const openModal = () => {
    setError('');
    setCloseResult(null);
    setView('status');
    setShowModal(true);
    fetchCurrent();
  };

  const closeModal = () => {
    setShowModal(false);
    setOpeningAmount('');
    setCountedCash('');
    setNotes('');
    setError('');
  };

  const handleOpenCaja = async () => {
    const amount = parseFloat(openingAmount);
    if (isNaN(amount) || amount < 0) {
      setError('Ingresa un monto inicial válido.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/pos/cash-sessions/open`,
        { opening_amount: amount, opened_by_name: clientData?.name || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpeningAmount('');
      await fetchCurrent();
      setView('status');
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo abrir la caja.');
    } finally {
      setBusy(false);
    }
  };

  const handleCloseCaja = async () => {
    const counted = parseFloat(countedCash);
    if (isNaN(counted) || counted < 0) {
      setError('Ingresa el monto contado en efectivo.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/api/pos/cash-sessions/${session.id}/close`,
        { counted_cash: counted, notes: notes.trim(), closed_by_name: clientData?.name || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCloseResult(res.data);
      setView('close-result');
      setCountedCash('');
      setNotes('');
      fetchCurrent();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cerrar la caja.');
    } finally {
      setBusy(false);
    }
  };

  const expectedNow = session
    ? Number(session.opening_amount || 0) + Number(liveSummary?.cash_sales_total || 0)
    : 0;

  return (
    <>
      {!loadingStatus && (
        <button
          onClick={openModal}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition ${
            session
              ? 'bg-green-50 border-green-400 text-green-700 hover:bg-green-100'
              : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {session ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
          {session ? 'Caja Abierta' : 'Caja Cerrada'}
        </button>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[85] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" /> Cierre de Caja
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
                </div>
              )}

              {view === 'status' && !session && (
                <div className="text-center py-4">
                  <Lock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-600 font-semibold">No hay una caja abierta.</p>
                  <p className="text-xs text-gray-400 mt-1">Ábrela con el monto inicial en efectivo antes de empezar a cobrar.</p>
                  <button
                    onClick={() => setView('open-form')}
                    className="mt-4 w-full py-2.5 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 transition"
                  >
                    Abrir Caja
                  </button>
                </div>
              )}

              {view === 'status' && session && (
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                    <p className="text-green-800 font-semibold">Caja abierta desde {formatDateTime(session.opened_at)}</p>
                    {session.opened_by_name && <p className="text-green-700 text-xs">Por: {session.opened_by_name}</p>}
                    <p className="text-green-700 text-xs">Monto inicial: ${Number(session.opening_amount).toFixed(2)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Ventas efectivo</p>
                      <p className="font-bold text-gray-900">${Number(liveSummary?.cash_sales_total || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Ventas transferencia</p>
                      <p className="font-bold text-gray-900">${Number(liveSummary?.transfer_sales_total || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Total vendido</p>
                      <p className="font-bold text-gray-900">${Number(liveSummary?.total_sales_amount || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500"># Ventas</p>
                      <p className="font-bold text-gray-900">{liveSummary?.total_sales_count || 0}</p>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                    <p className="text-blue-800">Efectivo esperado en caja ahora mismo: <span className="font-bold">${expectedNow.toFixed(2)}</span></p>
                  </div>
                  <button
                    onClick={() => setView('close-form')}
                    className="w-full py-2.5 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition"
                  >
                    Cerrar Caja
                  </button>
                </div>
              )}

              {view === 'open-form' && (
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700">
                    ¿Con cuánto efectivo abres la caja?
                  </label>
                  <input
                    type="number" step="0.01" autoFocus
                    value={openingAmount}
                    onChange={(e) => setOpeningAmount(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 rounded-lg text-lg"
                    placeholder="0.00"
                  />
                  <div className="flex gap-3">
                    <button onClick={() => setView('status')} className="flex-1 py-2.5 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-100">
                      Cancelar
                    </button>
                    <button onClick={handleOpenCaja} disabled={busy} className="flex-1 py-2.5 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 disabled:opacity-60">
                      {busy ? 'Abriendo...' : 'Confirmar'}
                    </button>
                  </div>
                </div>
              )}

              {view === 'close-form' && session && (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    Efectivo esperado: <span className="font-bold">${expectedNow.toFixed(2)}</span>
                    <span className="text-xs block text-blue-600 mt-0.5">(monto inicial ${Number(session.opening_amount).toFixed(2)} + ventas en efectivo ${Number(liveSummary?.cash_sales_total || 0).toFixed(2)})</span>
                  </div>
                  <label className="block text-sm font-semibold text-gray-700">
                    Cuenta el efectivo físico en caja ahora — ¿cuánto hay?
                  </label>
                  <input
                    type="number" step="0.01" autoFocus
                    value={countedCash}
                    onChange={(e) => setCountedCash(e.target.value)}
                    className="w-full px-4 py-2.5 border-2 rounded-lg text-lg"
                    placeholder="0.00"
                  />
                  {countedCash !== '' && !isNaN(parseFloat(countedCash)) && (
                    <p className={`text-sm font-semibold ${Math.abs(parseFloat(countedCash) - expectedNow) < 0.01 ? 'text-green-600' : parseFloat(countedCash) < expectedNow ? 'text-red-600' : 'text-blue-600'}`}>
                      {Math.abs(parseFloat(countedCash) - expectedNow) < 0.01
                        ? '✓ Cuadra exacto'
                        : parseFloat(countedCash) < expectedNow
                          ? `Faltante: $${(expectedNow - parseFloat(countedCash)).toFixed(2)}`
                          : `Sobrante: $${(parseFloat(countedCash) - expectedNow).toFixed(2)}`}
                    </p>
                  )}
                  <label className="block text-sm font-semibold text-gray-700">Notas (opcional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-4 py-2 border-2 rounded-lg text-sm"
                    rows={2}
                    placeholder="Ej: faltó por un vuelto mal dado en la mañana"
                  />
                  <div className="flex gap-3">
                    <button onClick={() => setView('status')} className="flex-1 py-2.5 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-100">
                      Cancelar
                    </button>
                    <button onClick={handleCloseCaja} disabled={busy} className="flex-1 py-2.5 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 disabled:opacity-60">
                      {busy ? 'Cerrando...' : 'Confirmar Cierre'}
                    </button>
                  </div>
                </div>
              )}

              {view === 'close-result' && closeResult && (
                <div className="space-y-3">
                  <div className={`rounded-lg p-4 text-center border-2 ${
                    Math.abs(closeResult.difference) < 0.01 ? 'bg-green-50 border-green-300' :
                    closeResult.difference < 0 ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-300'
                  }`}>
                    <p className="text-sm text-gray-600">Diferencia</p>
                    <p className={`text-2xl font-black ${
                      Math.abs(closeResult.difference) < 0.01 ? 'text-green-700' :
                      closeResult.difference < 0 ? 'text-red-700' : 'text-blue-700'
                    }`}>
                      {closeResult.difference > 0 ? '+' : ''}${Number(closeResult.difference).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {Math.abs(closeResult.difference) < 0.01 ? 'Caja cuadrada' : closeResult.difference < 0 ? 'Faltante' : 'Sobrante'}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Esperado</p>
                      <p className="font-bold text-gray-900">${Number(closeResult.expected_cash).toFixed(2)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Contado</p>
                      <p className="font-bold text-gray-900">${Number(closeResult.counted_cash).toFixed(2)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Total vendido</p>
                      <p className="font-bold text-gray-900">${Number(closeResult.total_sales_amount).toFixed(2)}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500"># Ventas</p>
                      <p className="font-bold text-gray-900">{closeResult.total_sales_count}</p>
                    </div>
                  </div>
                  {closeResult.items_sold?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">Qué se vendió</p>
                      <div className="max-h-40 overflow-y-auto border rounded-lg divide-y">
                        {closeResult.items_sold.map((it, idx) => (
                          <div key={idx} className="flex justify-between px-3 py-1.5 text-sm">
                            <span className="text-gray-700">{it.quantity}x {it.name}</span>
                            <span className="font-semibold text-gray-900">${Number(it.subtotal).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <button onClick={closeModal} className="w-full py-2.5 bg-gray-700 text-white font-bold rounded-lg hover:bg-gray-800">
                    Entendido
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {closeResult && (
        <CierreCajaTicket
          data={closeResult}
          businessName={clientData?.business_name}
          widthKey={printWidthKey}
        />
      )}
    </>
  );
}
